import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { clickAsLink } from 'helpers/link_helpers';
import { fetchPerson } from 'components/contacts/people/api';

import ActionsDropdown from 'components/dropdowns/actions_dropdown';

import {
  addToGroup,
  deleteFromGroup,
  newGroup,
  startBulkDelete,
  startDeleteGroups,
  startEmailGroup,
  startEditGroups,
  startBulkTask,
  startAddLeadsToProjects,
  startBulkTagging,
  startNewCallList
} from '../store/actions/people';

import { startAddToCallList } from '../store/actions/call_lists';
import { toggleView as toggleViewDuplicates } from '../store/actions/duplicates';
import { retrievePeople } from '../store/actions/search';

import { getCallLists } from '../store/selectors/call_lists';
import { canFindDuplicates } from '../store/selectors/ui';

export class PeopleActionsDropdown extends React.Component {
  static propTypes = {
    aggregateView: PropTypes.bool.isRequired,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })).isRequired,
    canAccessCrm: PropTypes.bool.isRequired,
    canDelete: PropTypes.bool.isRequired,
    canGroup: PropTypes.bool,
    canManageTags: PropTypes.bool.isRequired,
    canViewProjects: PropTypes.bool.isRequired,
    currentUser: PropTypes.shape({
      callListsEnabled: PropTypes.bool
    }).isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })).isRequired,
    people: PropTypes.array.isRequired,
    retrievePeople: PropTypes.func.isRequired,
    selectedGroupIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    selectedPersonIds: PropTypes.array.isRequired,
    showFindDuplicatesOption: PropTypes.bool,
    startDeleteGroups: PropTypes.func.isRequired,
    startEditGroups: PropTypes.func.isRequired,
    onAddLeadsToProjects: PropTypes.func,
    onAddToCallList: PropTypes.func.isRequired,
    onAddToGroup: PropTypes.func.isRequired,
    onBulkDelete: PropTypes.func.isRequired,
    onBulkTagging: PropTypes.func.isRequired,
    onCreateCallList: PropTypes.func.isRequired,
    onCreateEmailGroup: PropTypes.func.isRequired,
    onCreateTasks: PropTypes.func.isRequired,
    onDeleteFromGroup: PropTypes.func.isRequired,
    onNewGroup: PropTypes.func.isRequired,
    onToggleFindDuplicates: PropTypes.func.isRequired,
  };

  findSelectedGroups() {
    const { groups, selectedGroupIds } = this.props;
    if (selectedGroupIds.length === 0) {
      return [];
    }

    return groups.filter(group => selectedGroupIds.includes(group.id));
  }

  getPerson(id) {
    return this.props.people.find(person => (
      person.id === id
    ));
  }

  handleAddToCallList = (list) => {
    const { onAddToCallList, selectedPersonIds } = this.props;
    onAddToCallList({ listId: list.id, contactIds: selectedPersonIds });
  };

  handleAddToGroup = (group) => {
    const { selectedPersonIds, onAddToGroup } = this.props;
    onAddToGroup(group, selectedPersonIds);
  };

  handleDeleteFromGroup = (group) => {
    const { retrievePeople, selectedPersonIds, onDeleteFromGroup } = this.props;
    onDeleteFromGroup(group, selectedPersonIds);
    retrievePeople();
  };

  handleFindDuplicates = () => {
    const { retrievePeople, onToggleFindDuplicates } = this.props;
    onToggleFindDuplicates(true);
    retrievePeople();
  }

  handleStartDeleteGroups = () => {
    this.props.startDeleteGroups(this.findSelectedGroups());
  };

  handleStartEditGroups = () => {
    this.props.startEditGroups(this.findSelectedGroups());
  };

  handleMergePeople = async (e) => {
    const { selectedPersonIds } = this.props;
    const personOne = this.getPerson(selectedPersonIds[0]);
    const redirectUrl = encodeURIComponent(window.location.href);

    let linkUrl = '';
    if (personOne) {
      linkUrl = personOne.mergePath;
    } else {
      const person = await fetchPerson(selectedPersonIds[0]);
      linkUrl = person.paths.merge;
    }
    linkUrl += `?person_ids=${JSON.stringify(this.props.selectedPersonIds)}&redirect_to=${redirectUrl}`;

    clickAsLink(window, linkUrl, e);
  };

  handleEmailToGroup = () => {
    const { selectedPersonIds, onCreateEmailGroup } = this.props;
    onCreateEmailGroup(selectedPersonIds);
  };

  isDisabled = () => {
    const { selectedGroupIds, selectedPersonIds, showFindDuplicatesOption } = this.props;

    if (showFindDuplicatesOption || selectedPersonIds.length > 0) return false;
    return selectedGroupIds.length === 0 || selectedGroupIds[0] === 'ungrouped';
  }

  render() {
    const {
      aggregateView,
      callLists,
      canAccessCrm,
      canDelete,
      canGroup,
      canManageTags,
      canViewProjects,
      currentUser,
      selectedPersonIds,
      showFindDuplicatesOption,
      groups,
      onAddLeadsToProjects,
      onBulkDelete,
      onBulkTagging,
      onCreateCallList,
      onCreateTasks,
      onNewGroup
    } = this.props;

    const selectedGroups = this.findSelectedGroups();

    return (
      <ActionsDropdown
        aggregateView={aggregateView}
        callLists={callLists}
        callListsEnabled={currentUser && currentUser.callListsEnabled}
        canAccessCrm={canAccessCrm}
        canDelete={canDelete}
        canFindDuplicates={showFindDuplicatesOption}
        canGroup={canGroup}
        canManageTags={canManageTags}
        canViewProjects={canViewProjects}
        disabled={this.isDisabled()}
        groups={groups}
        isPerson
        selectedGroupIds={selectedGroups.map(group => group.id)}
        selectedIds={selectedPersonIds}
        onAddLeadsToProjects={onAddLeadsToProjects}
        onAddToCallList={this.handleAddToCallList}
        onAddToGroup={this.handleAddToGroup}
        onBulkDelete={onBulkDelete}
        onBulkTagging={onBulkTagging}
        onCreateCallList={onCreateCallList}
        onCreateGroup={onNewGroup}
        onCreateTasks={onCreateTasks}
        onDeleteFromGroup={this.handleDeleteFromGroup}
        onDeleteGroup={this.handleStartDeleteGroups}
        onEmailToGroup={this.handleEmailToGroup}
        onFindDuplicates={this.handleFindDuplicates}
        onMergeRecords={this.handleMergePeople}
        onUpdateGroup={this.handleStartEditGroups}
      />
    );
  }
}

const mapStateToProps = state => ({
  ...state.people,
  ...state.search.meta.people,
  ...state.static,
  aggregateView: state.static.aggregateView,
  callLists: getCallLists(state),
  currentUser: state.static.currentUser,
  canAccessCrm: state.ui.canAccessCrm,
  canDelete: state.ui.canDelete,
  canGroup: state.ui.canGroup,
  canManageTags: state.ui.canManageTags,
  canViewProjects: state.ui.canViewProjects,
  groups: state.people.groups,
  people: state.search.people,
  selectedGroupIds: state.query.peopleGroupIds,
  selectedPersonIds: state.selectList.selectedPersonIds,
  showFindDuplicatesOption: canFindDuplicates(state)
});

const mapDispatchToProps = {
  retrievePeople,
  startDeleteGroups,
  startEditGroups,

  onAddLeadsToProjects: startAddLeadsToProjects,
  onAddToCallList: startAddToCallList,
  onAddToGroup: addToGroup,
  onBulkDelete: startBulkDelete,
  onBulkTagging: startBulkTagging,
  onCreateCallList: startNewCallList,
  onCreateEmailGroup: startEmailGroup,
  onCreateTasks: startBulkTask,
  onDeleteFromGroup: deleteFromGroup,
  onNewGroup: newGroup,
  onToggleFindDuplicates: toggleViewDuplicates,
};

export default connect(mapStateToProps, mapDispatchToProps)(PeopleActionsDropdown);
